import React, { useState, useEffect } from 'react';
import {
    History, ArrowLeft, Loader2, Calendar, CheckCircle, Clock,
    TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp, Eye
} from 'lucide-react';
import BackNav from './components/BackNav';
import apiService from '../../../services/api';

// Domain names mapping
const DOMAIN_NAMES = {
    "D1": "Curriculum, Pedagogy & Assessment",
    "D2": "Infrastructure",
    "D3": "Human Resources",
    "D4": "Inclusive Practices",
    "D5": "Management & Governance",
    "D6": "Leadership",
    "D7": "Beneficiary Satisfaction"
};

const SQAAFHistory = ({ onBack, onViewAudit }) => {
    const [history, setHistory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expandedAudit, setExpandedAudit] = useState(null);

    useEffect(() => {
        loadHistory();
    }, []);

    const loadHistory = async () => {
        try {
            setLoading(true);
            const data = await apiService.sqaaf.getAuditHistory();
            setHistory(data);
            // Expand first audit by default
            if (data?.audits?.length > 0) {
                setExpandedAudit(data.audits[0].audit_id);
            }
        } catch (error) {
            console.error('Failed to load history:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'submitted':
                return (
                    <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        <CheckCircle className="w-3 h-3" />
                        Submitted
                    </span>
                );
            case 'in_progress':
                return (
                    <span className="flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                        <Clock className="w-3 h-3" />
                        In Progress
                    </span>
                );
            default:
                return (
                    <span className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                        Not Started
                    </span>
                );
        }
    };

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-green-600';
        if (score >= 60) return 'text-amber-600';
        return 'text-red-600';
    };

    const getLevelColor = (level) => {
        if (level >= 3) return 'bg-green-100 text-green-700';
        if (level >= 2) return 'bg-amber-100 text-amber-700';
        return 'bg-red-100 text-red-700';
    };

    const getChange = (currentAudit, prevAudit, domainId) => {
        if (!prevAudit?.domain_scores?.[domainId] || !currentAudit?.domain_scores?.[domainId]) {
            return null;
        }
        const current = currentAudit.domain_scores[domainId]?.average_level || 0;
        const prev = prevAudit.domain_scores[domainId]?.average_level || 0;
        return current - prev;
    };

    const renderTrend = (change) => {
        if (change === null) return <span className="text-gray-400">--</span>;
        if (change > 0) {
            return (
                <span className="flex items-center gap-1 text-green-600 font-medium">
                    <TrendingUp className="w-4 h-4" />
                    +{change.toFixed(1)}
                </span>
            );
        }
        if (change < 0) {
            return (
                <span className="flex items-center gap-1 text-red-600 font-medium">
                    <TrendingDown className="w-4 h-4" />
                    {change.toFixed(1)}
                </span>
            );
        }
        return (
            <span className="flex items-center gap-1 text-gray-500 font-medium">
                <Minus className="w-4 h-4" />
                0.0
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-[#D67C2C]" />
                <span className="ml-3 text-gray-600">Loading History...</span>
            </div>
        );
    }

    return (
        <div className="p-6 w-full">
            {/* Header */}
            <BackNav onBack={onBack} />
            <div className="flex items-center gap-3 mt-6 mb-8">                <div>
                    <h1 className="text-2xl font-bold text-[#1D3469] flex items-center gap-2">
                        <History className="w-7 h-7 text-[#D67C2C]" />
                        Assessment History
                    </h1>
                    <p className="text-gray-500">Year-over-year SQAAF assessments with domain comparison</p>
                </div>
            </div>

            {/* History List */}
            {(!history?.audits || history.audits.length === 0) ? (
                <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                    <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600">No Previous Assessments</h3>
                    <p className="text-gray-400 mt-2">Complete your first SQAAF assessment to build your history.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {history.audits.map((audit, idx) => {
                        const isExpanded = expandedAudit === audit.audit_id;
                        const prevAudit = history.audits[idx + 1]; // Previous year (sorted desc)

                        return (
                            <div
                                key={audit.audit_id}
                                className={`bg-white rounded-xl border overflow-hidden transition-all ${idx === 0 ? 'border-[#D67C2C] shadow-lg' : 'border-gray-200'
                                    }`}
                            >
                                {/* Audit Header */}
                                <button
                                    onClick={() => setExpandedAudit(isExpanded ? null : audit.audit_id)}
                                    className="w-full flex items-center justify-between p-5 hover:bg-gray-50"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${idx === 0 ? 'bg-[#D67C2C] text-white' : 'bg-gray-100 text-gray-600'
                                            }`}>
                                            <Calendar className="w-6 h-6" />
                                        </div>
                                        <div className="text-left">
                                            <h3 className="font-semibold text-[#1D3469] text-lg flex items-center gap-2">
                                                {audit.academic_year}
                                                {idx === 0 && (
                                                    <span className="text-xs bg-[#D67C2C] text-white px-2 py-0.5 rounded">
                                                        Current
                                                    </span>
                                                )}
                                            </h3>
                                            <div className="flex items-center gap-4 mt-1">
                                                {getStatusBadge(audit.status)}
                                                {audit.submitted_at && (
                                                    <span className="text-xs text-gray-400">
                                                        Submitted: {new Date(audit.submitted_at).toLocaleDateString()}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-6">
                                        {audit.total_score !== undefined && (
                                            <div className="text-right">
                                                <p className="text-xs text-gray-400">Overall Score</p>
                                                <p className={`text-3xl font-bold ${getScoreColor(audit.total_score)}`}>
                                                    {audit.total_score?.toFixed(1) || 0}%
                                                </p>
                                            </div>
                                        )}

                                        {isExpanded ? (
                                            <ChevronUp className="w-5 h-5 text-gray-400" />
                                        ) : (
                                            <ChevronDown className="w-5 h-5 text-gray-400" />
                                        )}
                                    </div>
                                </button>

                                {/* Expanded Domain Details */}
                                {isExpanded && (
                                    <div className="border-t border-gray-100 p-5 bg-gray-50">
                                        <h4 className="font-semibold text-[#1D3469] mb-4 flex items-center gap-2">
                                            <Eye className="w-5 h-5" />
                                            Domain-wise Performance
                                        </h4>

                                        <div className="overflow-x-auto">
                                            {(!audit.domain_scores || Object.keys(audit.domain_scores).length === 0) ? (
                                                <div className="py-8 text-center">
                                                    <p className="text-sm text-gray-400">
                                                        {audit.status === 'submitted'
                                                            ? 'Domain scores not available for this audit.'
                                                            : 'Domain scores are calculated after the audit is submitted.'}
                                                    </p>
                                                </div>
                                            ) : (
                                            <table className="w-full">
                                                <thead>
                                                    <tr className="text-left text-sm text-gray-500 border-b border-gray-200">
                                                        <th className="pb-3 font-medium">Domain</th>
                                                        <th className="pb-3 font-medium text-center">Level</th>
                                                        <th className="pb-3 font-medium text-center">Weighted Score</th>
                                                        {prevAudit && (
                                                            <th className="pb-3 font-medium text-center">vs {prevAudit.academic_year}</th>
                                                        )}
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {Object.entries(audit.domain_scores || {}).map(([domainId, score]) => {
                                                        const change = getChange(audit, prevAudit, domainId);
                                                        return (
                                                            <tr key={domainId} className="hover:bg-white transition-colors">
                                                                <td className="py-3">
                                                                    <div>
                                                                        <span className="font-medium text-[#1D3469]">{domainId}</span>
                                                                        <p className="text-xs text-gray-500 mt-0.5">
                                                                            {DOMAIN_NAMES[domainId] || score.domain_name}
                                                                        </p>
                                                                    </div>
                                                                </td>
                                                                <td className="py-3 text-center">
                                                                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${getLevelColor(score.average_level)}`}>
                                                                        {score.average_level?.toFixed(1) || '--'}
                                                                    </span>
                                                                </td>
                                                                <td className="py-3 text-center">
                                                                    <span className="font-medium text-gray-700">
                                                                        {score.weighted_score?.toFixed(1) || '--'}
                                                                    </span>
                                                                    <span className="text-gray-400 text-xs ml-1">
                                                                        / {score.weightage}
                                                                    </span>
                                                                </td>
                                                                {prevAudit && (
                                                                    <td className="py-3 text-center">
                                                                        {renderTrend(change)}
                                                                    </td>
                                                                )}
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                            )}
                                        </div>

                                        {/* Summary Stats */}
                                        {audit.domain_scores && Object.keys(audit.domain_scores).length > 0 && (
                                            <div className="mt-6 grid grid-cols-3 gap-4">
                                                <div className="bg-white p-4 rounded-lg border border-gray-200">
                                                    <p className="text-sm text-gray-500">Highest Domain</p>
                                                    <p className="font-bold text-green-600 text-lg mt-1">
                                                        {(() => {
                                                            const highest = Object.entries(audit.domain_scores)
                                                                .sort((a, b) => (b[1].average_level || 0) - (a[1].average_level || 0))[0];
                                                            return highest ? `${highest[0]} (${highest[1].average_level?.toFixed(1)})` : '--';
                                                        })()}
                                                    </p>
                                                </div>
                                                <div className="bg-white p-4 rounded-lg border border-gray-200">
                                                    <p className="text-sm text-gray-500">Lowest Domain</p>
                                                    <p className="font-bold text-red-600 text-lg mt-1">
                                                        {(() => {
                                                            const lowest = Object.entries(audit.domain_scores)
                                                                .sort((a, b) => (a[1].average_level || 0) - (b[1].average_level || 0))[0];
                                                            return lowest ? `${lowest[0]} (${lowest[1].average_level?.toFixed(1)})` : '--';
                                                        })()}
                                                    </p>
                                                </div>
                                                <div className="bg-white p-4 rounded-lg border border-gray-200">
                                                    <p className="text-sm text-gray-500">Average Level</p>
                                                    <p className="font-bold text-[#D67C2C] text-lg mt-1">
                                                        {(() => {
                                                            const levels = Object.values(audit.domain_scores).map(s => s.average_level || 0);
                                                            const avg = levels.reduce((a, b) => a + b, 0) / levels.length;
                                                            return avg.toFixed(2);
                                                        })()}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default SQAAFHistory;
